import type { CoraPublicSettings } from "@/interfaces/cora";

/**
 * Cora chat limits and copy. The limits mirror CORA_LIMITS in apps/gfgf-api/src/constants/cora.ts
 * (and the answer cap in apps/gfgf-api/src/dto/cora.ts) — duplicated by design (no shared package);
 * edit both together.
 */
export const CORA_UI = {
  MESSAGE_MAX_CHARS: 1000,
  HISTORY_MAX_TURNS: 20,
  HISTORY_TEXT_MAX_CHARS: 1000,
  ANSWER_MAX_CHARS: 2000,
  MIN_TYPING_MS: 700,
  AVATAR_SRC: "/images/cora-avatar.jpg",
  ASK_CORA: "Ask Cora",
  TALK_TO_HUMAN: "Or talk to a human",
  BACK_TO_CORA: "Back to Cora",
  OPEN_ARTICLE: "Open this article",
  COMPOSER_PLACEHOLDER: "Ask Cora anything…",
  COMPOSER_LABEL: "Ask Cora",
  SEND_LABEL: "Send",
  TYPING_LABEL: "Cora is thinking…",
  NEW_CONVERSATION: "Start a new conversation",
  UNAVAILABLE: "Cora is unavailable right now — please try again in a moment.",
  HANDOFF_SENT:
    "Passed to the team. They have your question and the screen you were on, and will reply by email.",
  HANDOFF_NOT_FILED: "I couldn't reach our team just now. Please email us at",
  SUPPORT_EMAIL: "support@giftsforgood.com",
  TICKET_INTRO: "To help our team, please tell us:",
  TICKET_SCREENSHOT_LABEL: "Screenshot (optional)",
  TICKET_SUBMIT: "Send to our team",
  TICKET_SENT: "Sent — our team will reply by email.",
  TICKET_SCREENSHOT_INVALID: "That screenshot is too large (5 MB max) or not a PNG/JPEG.",
  TICKET_FAILED: "Something went wrong sending this. Please try again.",
} as const;

/**
 * What the chat shows while GET /api/cora/settings loads or if it fails. Mirrors
 * CORA_PUBLIC_SETTINGS_DEFAULTS in apps/gfgf-api/src/constants/cora.ts (default seeds only);
 * edit both together.
 */
export const CORA_SETTINGS_FALLBACK: CoraPublicSettings = {
  assistantName: "Cora",
  tagline: "Our AI gifting agent",
  greetingTitle: "Hey, I’m Cora 👋",
  greetingBody:
    "I’ve read every article in this help center and can give you quick answers if you need them.",
  humanPrompt:
    "Happy to put you in touch. What’s your question? I’ll pass it on with the screen you’re on, so it reaches the right person first time.",
  seeds: {
    default: [
      "How do gift credits work?",
      "Can I pause a campaign?",
      "What happens to credits I don’t use?",
    ],
  },
  supportHours: {
    timezoneLabel: "ET",
    weekdayOpen: "9am",
    weekdayClose: "9pm",
    outOfHoursMessage:
      "Our team is offline right now and will reply by email when they are back, Monday to Friday, 9am–9pm ET.",
  },
  teamOnline: true,
};

/** Mirrors the API's screenshot limits for POST /api/cora/ticket; edit both together. */
export const CORA_SCREENSHOT_UI = {
  MAX_BYTES: 5 * 1024 * 1024,
  ACCEPT: "image/png,image/jpeg",
} as const;
